import { useApiPut, useApiQuery } from '@/api/main/query'
import { PageHeaderWrapper } from '@/app/layouts/PageHeaderWrapper'
import { FarmModel, FarmWorkIndicators, UpdateFarmModel } from '@/entities/farm'
import {
  DialogAttachRequisitToFarm,
  DialogFarmActions,
  farmsAPI,
  FarmStatsBanner,
} from '@/features/farm'

import { BaseButton, BaseCard, FarmIcon } from '@/shared/components'
import { useToast } from '@/shared/hooks'
import { useIsMobile } from '@/shared/utils'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AdminRequisitFarmDetailPayload } from 'syncra-api-client'
import { DateRange } from 'react-day-picker'
import { FarmRequisitesTable } from '@/features/farm/ui/FarmRequisiteTable'
import { ListIcon,PlayIcon, PauseIcon, GaugeIcon as LimitIcon } from 'lucide-react'
import { useFarmRequisites } from '@/features/requisites'

export const FarmPage = () => {
  const { id } = useParams<{ id: string }>()
  const isMobile = useIsMobile()
  const { addToast } = useToast()
  const queryClient = useQueryClient()
  const today = new Date()
  const utcToday = new Date(Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()))
  const [modalFarmActionsVisible, setModalFarmActionsVisible] = useState(false)

  // Устанавливаем начальный диапазон: сегодня - сегодня
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: utcToday,
    to: utcToday,
  })

  const { mutate: updateFarm } = useApiPut<FarmModel, UpdateFarmModel>(farmsAPI.updateFarm, {
    onSuccess: () => {
      addToast('Успех!', 'Ферма обновлена', 'success')
      queryClient.invalidateQueries({ queryKey: ['farm', id] })
      setModalFarmActionsVisible(false)
    },
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
  })

  const { mutate: disableFarm } = useApiPut(farmsAPI.disableFarm(id ?? ''), {
    onSuccess: () => {
      addToast('Успех!', 'Ферма остановлена', 'success')
      queryClient.invalidateQueries({ queryKey: ['farm', id] })
    },
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
  })

  const { mutate: enableFarm } = useApiPut(farmsAPI.enableFarm(id ?? ''), {
    onSuccess: () => {
      addToast('Успех!', 'Ферма включена', 'success')
      queryClient.invalidateQueries({ queryKey: ['farm', id] })
    },
    onError: (error) => {
      addToast('Ошибка!', error.message, 'error')
    },
  })

  // Функция для преобразования DateRange в ISO строки для API
  const apiDateRange = useMemo(() => {
    if (!dateRange?.from) {
      return {
        startDate: utcToday.toISOString(),
        endDate: utcToday.toISOString(),
      }
    }

    const startDate = new Date(
      Date.UTC(
        dateRange.from.getFullYear(),
        dateRange.from.getMonth(),
        dateRange.from.getDate(),
        0,
        0,
        0,
        0
      )
    )

    const endDate = dateRange.to
      ? new Date(
          Date.UTC(
            dateRange.to.getFullYear(),
            dateRange.to.getMonth(),
            dateRange.to.getDate(),
            23,
            59,
            59,
            999
          )
        )
      : new Date(
          Date.UTC(
            dateRange.from.getFullYear(),
            dateRange.from.getMonth(),
            dateRange.from.getDate(),
            23,
            59,
            59,
            999
          )
        )

    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
    }
  }, [dateRange, utcToday])

  const { data, isLoading, refetch } = useApiQuery<AdminRequisitFarmDetailPayload>(
    ['farm', id],
    farmsAPI.getFarmRequisite(),
    {
      farmId: id,
      startDate: apiDateRange.startDate,
      endDate: apiDateRange.endDate,
    }
  )

  const farmData = data?.farm

  useEffect(() => {
    if (id) {
      refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

    const {
    requisites,
    isLoading: isAttachLoading,
    modalVisible: modalAttachVisible,
    openModal: handleOpenAttachModal,
    closeModal: handleCloseAttachModal,
    selectedStatus,
    setSelectedStatus,
    setSearch,
    handleAttach,
  } = useFarmRequisites(id)

  if (isLoading) {
    return <div className="text-center py-8">Загрузка...</div>
  }

  if (!farmData?.farm) {
    return <div className="text-center py-8">Ферма не найдена</div>
  }

  const getFarmStatus = () => {
    if (farmData?.farm?.isBlocked || farmData?.farm?.isDisabledByUser) return 'stopped'
    if (farmData?.farm?.isDeleted) return 'canceled'
    return 'active'
  }

  const handleFarmSubmit = (data: UpdateFarmModel) => {
    updateFarm(data)
  }

  const handleOpenModal = () => {
    setModalFarmActionsVisible(true)
  }

  const handleDisableFarm = () => {
    disableFarm(id)
  }

  const handleEnableFarm = () => {
    enableFarm(id)
  }

  return (
    <div className="flex flex-col gap-7 md:gap-8 w-full">
      <PageHeaderWrapper
        icon={<FarmIcon status={getFarmStatus()} className="w-[52px] h-[52px]" />}
        title={farmData.farm.name}
        subtitle={<FarmWorkIndicators data={farmData} />}
        actions={
          <>
            <BaseButton
              variant="setting"
              padding="none"
              radius="md"
              className="py-3 w-full md:w-[225px]"
              iconPosition="right"
              icon={<LimitIcon className="text-primary dark:text-primary-dark" />}
              onClick={handleOpenModal}
              isLoading={isLoading}
            >
              Настроить
            </BaseButton>
            <BaseButton
              variant="primary"
              padding="none"
              radius="md"
              className="w-full md:w-[225px]"
              iconPosition="right"
              icon={
                farmData.farm.isDisabledByUser ? (
                  <PlayIcon className="text-white" />
                ) : (
                  <PauseIcon className="text-white" />
                )
              }
              onClick={farmData.farm.isDisabledByUser ? handleEnableFarm : handleDisableFarm}
              isLoading={isLoading}
            >
              {farmData.farm.isDisabledByUser ? 'Запустить' : 'Остановить'}
            </BaseButton>
          </>
        }
        isLoading={isLoading}
      />

      {/* Stats Banner */}
      <div className="flex flex-col gap-6 w-full">
        <FarmStatsBanner
          farm={data as AdminRequisitFarmDetailPayload}
          isLoading={isLoading}
          dateRange={dateRange}
          onDateChange={setDateRange}
        />

        {/* Requisites Section */}
        <div>
          <BaseCard className="border-0 md:border-1" padding={isMobile ? 'none' : 'sm'}>
            <div className="flex md:flex-row flex-col justify-between md:items-center gap-4 pb-6 w-full">
              <span>Реквизиты</span>

              <div className="flex gap-2 md:gap-4 w-full md:w-fit">
                <BaseButton
                  variant="outlined"
                  padding="none"
                  radius="lg"
                  className="px-7 !ring-card-border dark:!ring-card-border-dark w-full min-w-0 !text-main-black dark:!text-white"
                  iconPosition="left"
                  icon={
                    <ListIcon className="text-text-secondary md:dark:text-primary-dark md:text-primary" />
                  }
                  onClick={handleOpenAttachModal}
                >
                  <span>Прикрепить</span>
                </BaseButton>
              </div>
            </div>
            <FarmRequisitesTable
              farmId={id}
              startDate={apiDateRange.startDate}
              endDate={apiDateRange.endDate}
            />
          </BaseCard>
        </div>
      </div>

      {/* Dialogs */}
      <DialogFarmActions<'update'>
        type="update"
        modalVisible={modalFarmActionsVisible}
        setModalVisible={setModalFarmActionsVisible}
        onSubmit={handleFarmSubmit}
        farmData={farmData.farm}
      />
      <DialogAttachRequisitToFarm
        requisites={requisites}
        isVisible={modalAttachVisible}
        onClose={handleCloseAttachModal}
        onSearch={setSearch}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        onAttach={handleAttach}
        isLoading={isAttachLoading}
      />
    </div>
  )
}
